/**
 * PTO helper utilities
 */
import { format, parseISO } from "date-fns";

/**
 * Returns Tailwind CSS classes for background and text color based on PTO status
 */
export function statusColor(status: string): { bg: string; text: string } {
  const s = (status || "").toLowerCase();
  switch (s) {
    case "approved":
      return { bg: "tw-bg-green-100", text: "tw-text-green-800" };
    case "balance_overridden":
      return { bg: "tw-bg-blue-100", text: "tw-text-blue-800" };
    case "pending":
      return { bg: "tw-bg-yellow-100", text: "tw-text-yellow-800" };
    case "rejected":
      return { bg: "tw-bg-red-100", text: "tw-text-red-800" };
    default:
      return { bg: "tw-bg-gray-100", text: "tw-text-gray-800" };
  }
}

/**
 * Returns a unique key for a contractor/user from a PTO request
 */
export function contractorKey(r: any): string {
  return (
    r?.worker_id || r?.user_email || r?.user_name || r?.user_id || "unknown"
  );
}

/**
 * Returns consistent color for a contractor based on their key
 * Uses a stable hash to map contractors to colors
 */
export function contractorColor(key: string): { bg: string; text: string } {
  const palette = [
    { bg: "tw-bg-blue-600", text: "tw-text-white" },
    { bg: "tw-bg-green-600", text: "tw-text-white" },
    { bg: "tw-bg-purple-600", text: "tw-text-white" },
    { bg: "tw-bg-amber-500", text: "tw-text-black" },
    { bg: "tw-bg-pink-600", text: "tw-text-white" },
    { bg: "tw-bg-teal-600", text: "tw-text-white" },
    { bg: "tw-bg-indigo-600", text: "tw-text-white" },
    { bg: "tw-bg-lime-600", text: "tw-text-black" },
    { bg: "tw-bg-rose-600", text: "tw-text-white" },
    { bg: "tw-bg-cyan-600", text: "tw-text-white" },
  ];
  let hash = 0;
  const str = String(key || "");
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }

  return palette[hash % palette.length];
}

/**
 * Returns color based on request status and type
 * Used when filters are set to "All"
 */
export function requestTypeStatusColor(
  status: string,
  requestType: string
): { bg: string; text: string } {
  const normalizedStatus = (status || "").toLowerCase();
  const normalizedType = (requestType || "vacation").toLowerCase();

  // Vacation colors
  if (normalizedType === "vacation") {
    switch (normalizedStatus) {
      case "pending":
        return { bg: "tw-bg-yellow-300", text: "tw-text-black" };
      case "rejected":
      case "cancelled":
        return { bg: "tw-bg-red-600", text: "tw-text-white" };
      case "approved":
      case "balance_overridden":
        return { bg: "tw-bg-green-600", text: "tw-text-white" };
      default:
        return { bg: "tw-bg-gray-400", text: "tw-text-black" };
    }
  }

  // Sick colors
  if (normalizedType === "sick") {
    switch (normalizedStatus) {
      case "pending":
        return { bg: "tw-bg-blue-400", text: "tw-text-black" };
      case "rejected":
      case "cancelled":
        return { bg: "tw-bg-red-600", text: "tw-text-white" };
      case "approved":
      case "balance_overridden":
        return { bg: "tw-bg-green-600", text: "tw-text-white" };
      default:
        return { bg: "tw-bg-gray-400", text: "tw-text-black" };
    }
  }

  // Default fallback
  return { bg: "tw-bg-gray-400", text: "tw-text-black" };
}

/**
 * Formats a date value as MM/dd/yyyy
 * Handles Date objects, ISO strings, and other date formats
 */
export function formatMDY(value: Date | string | null | undefined): string {
  if (!value) return "-";

  try {
    let date: Date;

    if (value instanceof Date) {
      date = value;
    } else if (typeof value === "string") {
      // Handle ISO strings or other date strings
      const str = value.slice(0, 10); // Get YYYY-MM-DD part
      date = parseISO(str);
    } else {
      return "-";
    }

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return format(date, "MM/dd/yyyy");
  } catch (error) {
    console.error("Error formatting date:", error);
    return "-";
  }
}

/**
 * Formats a date value as MM/dd/yyyy HH:mm:ss
 * Handles Date objects, ISO strings, and other datetime formats
 */
export function formatMDYWithTime(
  value: Date | string | null | undefined
): string {
  if (!value) return "-";

  try {
    let date: Date;

    if (value instanceof Date) {
      date = value;
    } else if (typeof value === "string") {
      // Handle ISO strings or other datetime strings
      date = parseISO(value);
    } else {
      return "-";
    }

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return format(date, "MM/dd/yyyy HH:mm:ss");
  } catch (error) {
    console.error("Error formatting datetime:", error);
    return "-";
  }
}

/**
 * Returns Tailwind text color classes based on PTO status and request type
 * Used for table views and list displays
 */
export function getStatusColor(status: string, requestType: string): string {
  const normalizedStatus = (status || "").toLowerCase();
  const normalizedType = (requestType || "vacation").toLowerCase();

  // Vacation colors
  if (normalizedType === "vacation") {
    switch (normalizedStatus) {
      case "pending":
        return "tw-text-yellow-400 tw-font-medium";
      case "rejected":
      case "cancelled":
        return "tw-text-red-600 tw-font-medium";
      case "approved":
      case "balance_overridden":
        return "tw-text-green-600 tw-font-medium";
      default:
        return "tw-text-gray-600";
    }
  }

  // Sick colors
  if (normalizedType === "sick") {
    switch (normalizedStatus) {
      case "pending":
        return "tw-text-blue-600 tw-font-medium";
      case "rejected":
      case "cancelled":
        return "tw-text-red-600 tw-font-medium";
      case "approved":
      case "balance_overridden":
        return "tw-text-green-600 tw-font-medium";
      default:
        return "tw-text-gray-600";
    }
  }

  // Default fallback
  return "tw-text-gray-600";
}

export function isFutureRequest(r: any): boolean {
  if (!r.start_date) return false;
  let startDate: Date;
  if (r.start_date instanceof Date) {
    startDate = r.start_date;
  } else if (typeof r.start_date === "string") {
    // Handle ISO strings or other date strings
    const str = r.start_date.slice(0, 10); // Get YYYY-MM-DD part
    startDate = parseISO(str);
  } else {
    return false;
  }
  startDate.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return startDate >= today;
}

export function isPastWeekRequest(request: any): boolean {
  if (!request.start_date) return false;
  let startDate: Date;
  if (request.start_date instanceof Date) {
    startDate = request.start_date;
  } else if (typeof request.start_date === "string") {
    // Handle ISO strings or other date strings
    const str = request.start_date.slice(0, 10); // Get YYYY-MM-DD part
    startDate = parseISO(str);
  } else {
    return false;
  }
  startDate.setHours(0, 0, 0, 0);
  const today = new Date();
  // Get last Sunday
  const lastSunday = new Date(today);
  lastSunday.setDate(today.getDate() - today.getDay() - 7);
  lastSunday.setHours(0, 0, 0, 0);
  // Get last Saturday
  const lastSaturday = new Date(lastSunday);
  lastSaturday.setDate(lastSunday.getDate() + 6);
  lastSaturday.setHours(23, 59, 59, 999);
  return startDate >= lastSunday && startDate <= lastSaturday;
}
